/**
 * imageOptimizer - Bunny CDN image optimization with device-aware quality tiers
 * Appends Bunny.net optimization classes based on context and device capability
 * Based on BurrowTV-LITE-1local template (line ~10052)
 */

import DeviceConfig from './DeviceConfig';

// Image size contexts
export const ImageSize = {
  THUMB: 'THUMB',       // Video grid thumbnails
  HERO: 'HERO',         // Featured carousel/hero images
  BG: 'BG',             // Background images
  AVATAR: 'AVATAR',     // User avatars
  DEFAULT: 'DEFAULT'    // No optimization
};

/**
 * Bunny CDN class mappings
 * Format: ?class=<className>
 * 
 * Bunny.net automatically serves optimized images based on these classes
 */
const BUNNY_CLASSES = {
  // Thumbnail classes (small video cards)
  thumblow: '400x225',   // Mobile/low-end
  thumbstd: '512x288',   // Desktop standard
  
  // Hero classes (featured/large displays)
  herolow: '640x360',    // Mobile/low-end
  herostd: '1024x576',   // Desktop standard
  herohigh: '1280x720',  // High-res displays
  
  // Background classes
  bglow: '1280x720',     // Mobile/low-end
  bgstd: '1920x1080',    // Desktop standard
  bghigh: '2560x1440',   // 4K displays
  
  // Avatar classes
  avatarsmall: '64x64',  // Tiny avatars
  avatarmed: '128x128',  // Standard avatars
  avatarlarge: '256x256' // Large profile images
};

/**
 * Get optimized Bunny CDN URL with appropriate class parameter
 * @param {string} url - Original CDN URL
 * @param {string} size - ImageSize constant (THUMB, HERO, BG, AVATAR)
 * @param {object} options - Additional options
 * @param {boolean} options.forceQuality - Force specific quality tier (low/std/high)
 * @returns {string} Optimized URL with Bunny class parameter
 */
export function getOptimizedImageUrl(url, size = ImageSize.DEFAULT, options = {}) {
  // Return as-is if not a URL or if optimization disabled
  if (!url || typeof url !== 'string') return url;
  if (size === ImageSize.DEFAULT) return url;
  
  // Get device tier
  const deviceTier = options.forceQuality || DeviceConfig.getImageTier();
  
  // Determine Bunny class based on size context and device tier
  let bunnyClass;
  
  switch (size) {
    case ImageSize.THUMB:
      bunnyClass = deviceTier === 'low' ? 'thumblow' : 'thumbstd';
      break;
      
    case ImageSize.HERO:
      if (deviceTier === 'low') bunnyClass = 'herolow';
      else if (deviceTier === 'high') bunnyClass = 'herohigh';
      else bunnyClass = 'herostd';
      break;
      
    case ImageSize.BG:
      if (deviceTier === 'low') bunnyClass = 'bglow';
      else if (deviceTier === 'high') bunnyClass = 'bghigh';
      else bunnyClass = 'bgstd';
      break;
      
    case ImageSize.AVATAR:
      // Use size option or default to medium
      if (options.avatarSize === 'small') bunnyClass = 'avatarsmall';
      else if (options.avatarSize === 'large') bunnyClass = 'avatarlarge';
      else bunnyClass = 'avatarmed';
      break;
      
    default:
      return url;
  }
  
  // Add class parameter to URL
  return appendBunnyCDNClass(url, bunnyClass);
}

/**
 * Append Bunny CDN class parameter to URL
 * Handles existing query params gracefully
 * @param {string} url - Original URL
 * @param {string} bunnyClass - Bunny optimization class name
 * @returns {string} URL with class parameter
 */
function appendBunnyCDNClass(url, bunnyClass) {
  if (!url || !bunnyClass) return url;
  
  try {
    // Parse the URL
    const urlObj = new URL(url, window.location.origin);
    
    // Add or update the class parameter
    urlObj.searchParams.set('class', bunnyClass);
    
    return urlObj.toString();
  } catch (error) {
    // Fallback for invalid URLs - simple string append
    const separator = url.includes('?') ? '&' : '?';
    return `${url}${separator}class=${bunnyClass}`;
  }
}

/**
 * Get optimized thumbnail URL for video cards
 * @param {string} url - Original thumbnail URL
 * @returns {string} Optimized thumbnail URL
 */
export function getOptimizedThumbnail(url) {
  return getOptimizedImageUrl(url, ImageSize.THUMB);
}

/**
 * Get optimized hero image URL for featured content
 * @param {string} url - Original hero image URL
 * @returns {string} Optimized hero image URL
 */
export function getOptimizedHeroImage(url) {
  return getOptimizedImageUrl(url, ImageSize.HERO);
}

/**
 * Get optimized background image URL
 * @param {string} url - Original background URL
 * @returns {string} Optimized background URL
 */
export function getOptimizedBackground(url) {
  return getOptimizedImageUrl(url, ImageSize.BG);
}

/**
 * Get optimized avatar URL
 * @param {string} url - Original avatar URL
 * @param {string} size - Avatar size: 'small', 'medium', 'large'
 * @returns {string} Optimized avatar URL
 */
export function getOptimizedAvatar(url, size = 'medium') {
  return getOptimizedImageUrl(url, ImageSize.AVATAR, { avatarSize: size });
}

/**
 * Batch optimize multiple image URLs
 * @param {Array<string>} urls - Array of image URLs
 * @param {string} size - ImageSize constant
 * @returns {Array<string>} Array of optimized URLs
 */
export function batchOptimizeImages(urls, size = ImageSize.THUMB) {
  if (!Array.isArray(urls)) return [];
  return urls.map(url => getOptimizedImageUrl(url, size));
}

/**
 * Check if URL is already optimized (has class parameter)
 * @param {string} url - URL to check
 * @returns {boolean} True if already has Bunny class parameter
 */
export function isAlreadyOptimized(url) {
  if (!url || typeof url !== 'string') return false;
  
  try {
    const urlObj = new URL(url, window.location.origin);
    return urlObj.searchParams.has('class');
  } catch (error) {
    return url.includes('?class=') || url.includes('&class=');
  }
}

/**
 * Get quality info for debugging
 * @returns {object} Current optimization settings
 */
export function getOptimizationInfo() {
  return {
    deviceTier: DeviceConfig.getImageTier(),
    device: DeviceConfig.device,
    lowEndDevice: DeviceConfig.lowEndDevice,
    isMobile: DeviceConfig.isMobile(),
    bunnyClasses: BUNNY_CLASSES
  };
}

export default {
  getOptimizedImageUrl,
  getOptimizedThumbnail,
  getOptimizedHeroImage,
  getOptimizedBackground,
  getOptimizedAvatar,
  batchOptimizeImages,
  isAlreadyOptimized,
  getOptimizationInfo,
  ImageSize
};
